import React, { useState, useEffect } from 'react';
import {
  Page,
  Card,
  Text,
  Button,
  Banner,
  BlockStack,
  InlineStack,
  Badge,
  Spinner,
  Layout,
  Tabs,
  TextField,
  Checkbox,
  Select,
  FormLayout,
  Modal
} from '@shopify/polaris';
import {
  RefreshIcon,
  DeleteIcon,
  KeyIcon
} from '@shopify/polaris-icons';
import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import { API_CONFIG } from '../config/api';
import axios from 'axios';

interface TenantSettings {
  id: number;
  name: string;
  created_at: string;
  updated_at: string;
}

interface AiSettings {
  auto_enabled_on_call: boolean;
  tenant_context?: string;
}

interface ApiKeyInfo {
  api_key_active: boolean;
  api_key?: string;
  api_key_created_at?: string;
  api_key_last_used_at?: string;
}

const LANGUAGE_OPTIONS = [
  { label: 'English', value: 'en' },
  { label: 'Spanish', value: 'es' },
  { label: 'French', value: 'fr' },
  { label: 'German', value: 'de' },
  { label: 'Italian', value: 'it' },
  { label: 'Portuguese', value: 'pt' },
  { label: 'Russian', value: 'ru' },
  { label: 'Chinese', value: 'zh' },
  { label: 'Japanese', value: 'ja' },
  { label: 'Korean', value: 'ko' }
];

const Settings: React.FC = () => {
  const { user, updateTenant } = useAuth();
  const { selectedClient } = useClient();

  // State for regular user settings
  const [settings, setSettings] = useState<TenantSettings | null>(null);
  const [formData, setFormData] = useState({ name: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // AI Settings state
  const [aiSettings, setAiSettings] = useState<AiSettings | null>(null);
  const [aiFormData, setAiFormData] = useState<AiSettings>({
    auto_enabled_on_call: false,
    tenant_context: ''
  });
  const [aiLoading, setAiLoading] = useState(false);
  const [aiSaving, setAiSaving] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiSuccess, setAiSuccess] = useState<string | null>(null);

  // API Key state
  const [apiKeyInfo, setApiKeyInfo] = useState<ApiKeyInfo | null>(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeySaving, setApiKeySaving] = useState(false);
  const [apiKeyError, setApiKeyError] = useState<string | null>(null);
  const [apiKeySuccess, setApiKeySuccess] = useState<string | null>(null);
  const [newApiKey, setNewApiKey] = useState<string>('');
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [showResetModal, setShowResetModal] = useState(false);
  const [showCopyToast, setShowCopyToast] = useState(false);

  // Tabs
  const [selectedTab, setSelectedTab] = useState(0);
  const regularUserTabs = [
    { id: 'general', content: 'General', panelID: 'general-content' },
    { id: 'api-keys', content: 'API Keys', panelID: 'api-keys-content' },
    { id: 'ai-settings', content: 'AI Settings', panelID: 'ai-settings-content' },
  ];
  const supportManagerTabs = [
    { id: 'general', content: 'General', panelID: 'general-content' },
    { id: 'ai-settings', content: 'AI Settings', panelID: 'ai-settings-content' },
  ];
  
  const tabs = user?.role === 'support_manager' ? supportManagerTabs : regularUserTabs;

  useEffect(() => {
    if (user?.role !== 'support_manager') {
      fetchSettings();
      fetchAiSettings();
      fetchApiKeyInfo();
    } else if (selectedClient) {
      fetchSupportSettings();
      fetchSupportAiSettings();
    } else {
      setLoading(false);
      setAiLoading(false);
      setApiKeyLoading(false);
    }
  }, [user?.role, selectedClient]); // Depend on user role and selected client

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.FULL_BASE_URL}/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const tenantData = response.data.data.tenant;
        setSettings(tenantData);
        setFormData({ name: tenantData.name });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchAiSettings = async () => {
    try {
      setAiLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.FULL_BASE_URL}/ai_settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const aiData = response.data.data.ai_settings;
        setAiSettings(aiData);
        setAiFormData(aiData);
      }
    } catch (err: any) {
      setAiError(err.response?.data?.error || 'Failed to load AI settings');
    } finally {
      setAiLoading(false);
    }
  };

  const fetchApiKeyInfo = async () => {
    try {
      setApiKeyLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.FULL_BASE_URL}/api_key`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setApiKeyInfo(response.data.data);
      }
    } catch (err: any) {
      setApiKeyError(err.response?.data?.error || 'Failed to load API key info');
    } finally {
      setApiKeyLoading(false);
    }
  };

  const fetchSupportSettings = async () => {
    if (!selectedClient) return;
    
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.FULL_BASE_URL}/support_settings?client_id=${selectedClient.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const tenantData = response.data.data.tenant;
        setSettings(tenantData);
        setFormData({ name: tenantData.name });
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load client settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportAiSettings = async () => {
    if (!selectedClient) return;
    
    try {
      setAiLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_CONFIG.FULL_BASE_URL}/support_settings/ai_settings?client_id=${selectedClient.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const aiData = response.data.data.ai_setting;
        setAiSettings(aiData);
        setAiFormData(aiData);
      }
    } catch (err: any) {
      setAiError(err.response?.data?.error || 'Failed to load client AI settings');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSubmit = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      const token = localStorage.getItem('token');
      const endpoint = user?.role === 'support_manager' 
        ? `${API_CONFIG.FULL_BASE_URL}/support_settings?client_id=${selectedClient?.id}`
        : `${API_CONFIG.FULL_BASE_URL}/settings`;
        
      const response = await axios.patch(endpoint, {
        tenant: formData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSuccess('Settings updated successfully!');
        if (user?.role !== 'support_manager') {
          updateTenant(response.data.data.tenant);
        }
      } else {
        setError(response.data.error || 'Failed to update settings');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const handleAiSettingsSubmit = async () => {
    try {
      setAiSaving(true);
      setAiError(null);
      setAiSuccess(null);

      const token = localStorage.getItem('token');
      const endpoint = user?.role === 'support_manager' 
        ? `${API_CONFIG.FULL_BASE_URL}/support_settings/update_ai_settings?client_id=${selectedClient?.id}`
        : `${API_CONFIG.FULL_BASE_URL}/ai_settings`;
        
      const response = await axios.patch(endpoint, {
        ai_settings: aiFormData
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAiSuccess('AI settings updated successfully!');
        setAiSettings(aiFormData);
      } else {
        setAiError(response.data.error || 'Failed to update AI settings');
      }
    } catch (err: any) {
      setAiError(err.response?.data?.error || 'Failed to update AI settings');
    } finally {
      setAiSaving(false);
    }
  };

  const handleGenerateApiKey = async () => {
    try {
      setApiKeySaving(true);
      setApiKeyError(null);
      setApiKeySuccess(null);

      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_CONFIG.FULL_BASE_URL}/api_key`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setApiKeySuccess('API key generated successfully!');
        setNewApiKey(response.data.data.api_key);
        await fetchApiKeyInfo();
      } else {
        setApiKeyError(response.data.error || 'Failed to generate API key');
      }
    } catch (err: any) {
      setApiKeyError(err.response?.data?.error || 'Failed to generate API key');
    } finally {
      setApiKeySaving(false);
    }
  };

  const handleResetApiKey = async () => {
    try {
      setApiKeySaving(true);
      setApiKeyError(null);
      setApiKeySuccess(null);

      const token = localStorage.getItem('token');
      const response = await axios.patch(`${API_CONFIG.FULL_BASE_URL}/api_key/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setApiKeySuccess('API key reset successfully!');
        setNewApiKey(response.data.data.api_key);
        await fetchApiKeyInfo();
      } else {
        setApiKeyError(response.data.error || 'Failed to reset API key');
      }
    } catch (err: any) {
      setApiKeyError(err.response?.data?.error || 'Failed to reset API key');
    } finally {
      setApiKeySaving(false);
    }
  };

  const handleRevokeApiKey = async () => {
    try {
      setApiKeySaving(true);
      setApiKeyError(null);
      setApiKeySuccess(null);

      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_CONFIG.FULL_BASE_URL}/api_key`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setApiKeySuccess('API key revoked successfully!');
        setNewApiKey('');
        await fetchApiKeyInfo();
      } else {
        setApiKeyError(response.data.error || 'Failed to revoke API key');
      }
    } catch (err: any) {
      setApiKeyError(err.response?.data?.error || 'Failed to revoke API key');
    } finally {
      setApiKeySaving(false);
      setShowRevokeModal(false);
    }
  };

  // Support Manager Settings View
  if (user?.role === 'support_manager') {
    return (
      <Page title="Client Settings" fullWidth>
        <Layout>
          {selectedClient && (
            <>
              <Layout.Section>
                <Card>
                  <Tabs
                    tabs={tabs}
                    selected={selectedTab}
                    onSelect={setSelectedTab}
                  >
                    {selectedTab === 0 && (
                      <Card>
                        <BlockStack gap="400">
                          <Text variant="headingMd" as="h2">
                            General Settings
                          </Text>
                          
                          {loading ? (
                            <div style={{ textAlign: 'center', padding: '1rem' }}>
                              <Spinner size="small" />
                              <Text variant="bodyMd" as="p">Loading client settings...</Text>
                            </div>
                          ) : (
                            <>
                              {error && (
                                <Banner tone="critical" onDismiss={() => setError(null)}>
                                  {error}
                                </Banner>
                              )}
                              
                              {success && (
                                <Banner tone="success" onDismiss={() => setSuccess(null)}>
                                  {success}
                                </Banner>
                              )}

                              <FormLayout>
                                <TextField
                                  label="Client Name"
                                  value={formData.name}
                                  onChange={(value) => setFormData({ ...formData, name: value })}
                                  autoComplete="off"
                                />
                                <Button
                                  variant="primary"
                                  loading={saving}
                                  onClick={handleSubmit}
                                >
                                  Save Changes
                                </Button>
                              </FormLayout>
                            </>
                          )}
                        </BlockStack>
                      </Card>
                    )}



                    {selectedTab === 1 && (
                      <Card>
                        <BlockStack gap="400">
                          <Text variant="headingMd" as="h2">
                            AI Settings
                          </Text>
                          
                          {aiLoading ? (
                            <div style={{ textAlign: 'center', padding: '1rem' }}>
                              <Spinner size="small" />
                              <Text variant="bodyMd" as="p">Loading AI settings...</Text>
                            </div>
                          ) : (
                            <>
                              {aiError && (
                                <Banner tone="critical" onDismiss={() => setAiError(null)}>
                                  {aiError}
                                </Banner>
                              )}
                              
                              {aiSuccess && (
                                <Banner tone="success" onDismiss={() => setAiSuccess(null)}>
                                  {aiSuccess}
                                </Banner>
                              )}

                              <FormLayout>
                                <Checkbox
                                  label="Auto Enable on Call"
                                  checked={aiFormData.auto_enabled_on_call}
                                  onChange={(checked) => setAiFormData(prev => ({ ...prev, auto_enabled_on_call: checked }))}
                                />
                                
                                <TextField
                                  label="Customer Context"
                                  value={aiFormData.tenant_context || ''}
                                  onChange={(value) => setAiFormData(prev => ({ ...prev, tenant_context: value }))}
                                  multiline={4}
                                  helpText="Optional context about your customers that will be added to AI prompts. This helps provide more relevant analysis."
                                  placeholder="e.g., We are a real estate company specializing in luxury properties. Our customers are typically high-net-worth individuals looking for premium homes."
                                  autoComplete="off"
                                />
                                
                                <InlineStack gap="200">
                                  <Button
                                    variant="primary"
                                    onClick={handleAiSettingsSubmit}
                                    loading={aiSaving}
                                  >
                                    Update AI Settings
                                  </Button>
                                </InlineStack>
                              </FormLayout>
                            </>
                          )}
                        </BlockStack>
                      </Card>
                    )}
                  </Tabs>
                </Card>
              </Layout.Section>
            </>
          )}
        </Layout>
      </Page>
    );
  }

  // Regular User Settings View
  if (loading) {
    return (
      <Page title="Settings" fullWidth>
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Spinner size="large" />
          <Text variant="bodyMd" as="p">Loading settings...</Text>
        </div>
      </Page>
    );
  }

  if (error) {
    return (
      <Page title="Settings" fullWidth>
        <Banner tone="critical" onDismiss={() => setError(null)}>
          {error}
        </Banner>
      </Page>
    );
  }

  return (
    <Page title="Settings" fullWidth>
      {success && (
        <Banner tone="success" onDismiss={() => setSuccess(null)}>
          {success}
        </Banner>
      )}

      <Layout>
        <Layout.Section>
          <Card>
            <Tabs
              tabs={tabs}
              selected={selectedTab}
              onSelect={setSelectedTab}
            >
              {selectedTab === 0 && (
                <Card>
                  <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">
                      Tenant Settings
                    </Text>
                    <FormLayout>
                      <TextField
                        label="Tenant Name"
                        value={formData.name}
                        onChange={(value) => setFormData({ ...formData, name: value })}
                        error={error || undefined}
                        autoComplete="off"
                      />
                      <Button
                        variant="primary"
                        loading={saving}
                        onClick={handleSubmit}
                      >
                        Save Changes
                      </Button>
                    </FormLayout>
                  </BlockStack>
                </Card>
              )}

              {selectedTab === 1 && (
                <Card>
                  <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">
                      API Key Management
                    </Text>
                    
                    {apiKeyLoading ? (
                      <div style={{ textAlign: 'center', padding: '1rem' }}>
                        <Spinner size="small" />
                        <Text variant="bodyMd" as="p">Loading API key info...</Text>
                      </div>
                    ) : (
                      <>
                        {apiKeyInfo?.api_key_active ? (
                          <BlockStack gap="300">
                            <Text variant="bodyMd" as="p">
                              <strong>API Key Status:</strong> Active
                            </Text>
                            
                            {apiKeyInfo.api_key_created_at && (
                              <Text variant="bodySm" as="p" tone="subdued">
                                Created: {new Date(apiKeyInfo.api_key_created_at).toLocaleString()}
                              </Text>
                            )}
                            
                            {apiKeyInfo.api_key_last_used_at && (
                              <Text variant="bodySm" as="p" tone="subdued">
                                Last Used: {new Date(apiKeyInfo.api_key_last_used_at).toLocaleString()}
                              </Text>
                            )}
                            
                            <InlineStack gap="200">
                              <Button
                                icon={RefreshIcon}
                                onClick={handleResetApiKey}
                                loading={apiKeySaving}
                              >
                                Reset API Key
                              </Button>
                              <Button
                                icon={DeleteIcon}
                                tone="critical"
                                onClick={() => setShowRevokeModal(true)}
                                loading={apiKeySaving}
                              >
                                Revoke API Key
                              </Button>
                            </InlineStack>
                          </BlockStack>
                        ) : (
                          <BlockStack gap="300">
                            <Text variant="bodyMd" as="p">
                              <strong>API Key Status:</strong> Not Active
                            </Text>
                            <Button
                              icon={KeyIcon}
                              onClick={handleGenerateApiKey}
                              loading={apiKeySaving}
                            >
                              Generate API Key
                            </Button>
                          </BlockStack>
                        )}

                        {/* API Code Samples */}
                        {apiKeyInfo?.api_key_active && (
                          <BlockStack gap="400">
                            <Text variant="headingMd" as="h3">
                              API Usage Examples
                            </Text>
                            
                            {/* cURL Example */}
                            <div>
                              <Text variant="bodyMd" as="p" fontWeight="semibold">cURL</Text>
                              <div style={{ 
                                backgroundColor: '#f6f6f7', 
                                padding: '12px', 
                                borderRadius: '6px',
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                overflowX: 'auto'
                              }}>
                                <pre style={{ margin: '0', whiteSpace: 'pre-wrap' }}>
{`curl -X POST "${API_CONFIG.FULL_BASE_URL}/api/v1/transcribe" \\
  -H "Authorization: Bearer ${apiKeyInfo.api_key}" \\
  -H "Content-Type: application/json" \\
  -d '{
    "audio_url": "https://example.com/audio.mp3",
    "language": "auto"
  }'`}
                                </pre>
                              </div>
                            </div>

                            {/* PHP Example */}
                            <div>
                              <Text variant="bodyMd" as="p" fontWeight="semibold">PHP</Text>
                              <div style={{ 
                                backgroundColor: '#f6f6f7', 
                                padding: '12px', 
                                borderRadius: '6px',
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                overflowX: 'auto'
                              }}>
                                <pre style={{ margin: '0', whiteSpace: 'pre-wrap' }}>
{`<?php
$apiKey = '${apiKeyInfo.api_key}';
$url = '${API_CONFIG.FULL_BASE_URL}/api/v1/transcribe';

$data = [
    'audio_url' => 'https://example.com/audio.mp3',
    'language' => 'auto'
];

$ch = curl_init();
curl_setopt($ch, CURLOPT_URL, $url);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode($data));
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Authorization: Bearer ' . $apiKey,
    'Content-Type: application/json'
]);
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);

$response = curl_exec($ch);
curl_close($ch);

echo $response;`}
                                </pre>
                              </div>
                            </div>

                            {/* Ruby Example */}
                            <div>
                              <Text variant="bodyMd" as="p" fontWeight="semibold">Ruby</Text>
                              <div style={{ 
                                backgroundColor: '#f6f6f7', 
                                padding: '12px', 
                                borderRadius: '6px',
                                fontFamily: 'monospace',
                                fontSize: '14px',
                                overflowX: 'auto'
                              }}>
                                <pre style={{ margin: '0', whiteSpace: 'pre-wrap' }}>
{`require 'net/http'
require 'json'

api_key = '${apiKeyInfo.api_key}'
url = URI('${API_CONFIG.FULL_BASE_URL}/api/v1/transcribe')

data = {
  audio_url: 'https://example.com/audio.mp3',
  language: 'auto'
}

http = Net::HTTP.new(url.host, url.port)
http.use_ssl = true

request = Net::HTTP::Post.new(url)
request['Authorization'] = "Bearer \#{api_key}"
request['Content-Type'] = 'application/json'
request.body = data.to_json

response = http.request(request)
puts response.body`}
                                </pre>
                              </div>
                            </div>
                          </BlockStack>
                        )}

                        {apiKeyError && (
                          <Banner tone="critical" onDismiss={() => setApiKeyError(null)}>
                            {apiKeyError}
                          </Banner>
                        )}
                        
                        {apiKeySuccess && (
                          <Banner tone="success" onDismiss={() => setApiKeySuccess(null)}>
                            {apiKeySuccess}
                          </Banner>
                        )}
                      </>
                    )}
                  </BlockStack>
                </Card>
              )}

              {selectedTab === 2 && (
                <Card>
                  <BlockStack gap="400">
                    <Text variant="headingMd" as="h2">
                      AI Settings
                    </Text>
                    
                    {aiLoading ? (
                      <div style={{ textAlign: 'center', padding: '1rem' }}>
                        <Spinner size="small" />
                        <Text variant="bodyMd" as="p">Loading AI settings...</Text>
                      </div>
                    ) : (
                      <>
                        {aiError && (
                          <Banner tone="critical" onDismiss={() => setAiError(null)}>
                            {aiError}
                          </Banner>
                        )}
                        
                        {aiSuccess && (
                          <Banner tone="success" onDismiss={() => setAiSuccess(null)}>
                            {aiSuccess}
                          </Banner>
                        )}

                        <FormLayout>
                          <Checkbox
                            label="Auto Enable on Call"
                            checked={aiFormData.auto_enabled_on_call}
                            onChange={(checked) => setAiFormData(prev => ({ ...prev, auto_enabled_on_call: checked }))}
                          />
                          
                          <TextField
                            label="Customer Context"
                            value={aiFormData.tenant_context || ''}
                            onChange={(value) => setAiFormData(prev => ({ ...prev, tenant_context: value }))}
                            multiline={4}
                            helpText="Optional context about your customers that will be added to AI prompts. This helps provide more relevant analysis."
                            placeholder="e.g., We are a real estate company specializing in luxury properties. Our customers are typically high-net-worth individuals looking for premium homes."
                            autoComplete="off"
                          />
                          
                          <InlineStack gap="200">
                            <Button
                              variant="primary"
                              onClick={handleAiSettingsSubmit}
                              loading={aiSaving}
                            >
                              Update AI Settings
                            </Button>
                          </InlineStack>
                        </FormLayout>
                      </>
                    )}
                  </BlockStack>
                </Card>
              )}
            </Tabs>
          </Card>
        </Layout.Section>
      </Layout>

      {/* Revoke API Key Modal */}
      <Modal
        open={showRevokeModal}
        onClose={() => setShowRevokeModal(false)}
        title="Revoke API Key"
        primaryAction={{
          content: 'Revoke',
          onAction: handleRevokeApiKey,
          loading: apiKeySaving,
          destructive: true
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: () => setShowRevokeModal(false)
          }
        ]}
      >
        <Modal.Section>
          <Text variant="bodyMd" as="p">
            Are you sure you want to revoke the API key? This action cannot be undone and will immediately invalidate the current API key.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
};

export default Settings;
